/*
 * Transliterating what is on screen, rather than editing 161 files.
 *
 * The alternative was wrapping every literal in t(). That is ~1,278 edits
 * across the whole app, each one a chance to put a call in the wrong place,
 * and it would have to be repeated by hand on every new screen. This walks the
 * rendered text instead: one implementation, every page covered, and a screen
 * added tomorrow is covered too.
 *
 * Only Text nodes and a short list of user-visible attributes are touched.
 * Input values are never touched, so nothing typed at the desk is altered.
 * React owns the DOM, so a re-render puts English back; the observer sees that
 * and re-applies.
 */

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE']);
// An escape hatch for anything that must stay as written — a brand wordmark,
// a customer's own words, an invoice number the customer will read back to us.
const SKIP_SELECTOR = '[data-no-i18n]';
const ATTRS = ['placeholder', 'title', 'aria-label'];

// Remember the English, so a second pass over an already-transliterated node
// does not try to look up Telugu in an English-keyed dictionary.
const ORIGINAL = new WeakMap();

// Values that arrive from the API are often lower-cased ("owner" for the role
// the settings screen calls "Owner"), so fall back to a case-insensitive hit
// and keep the original casing of the first letter.
let FOLDED = null;
// Switching back to English has to put the English back, so keep a list of
// what we touched. Nodes React has since discarded are simply no longer in
// the document; restoring them is harmless.
let TOUCHED = [];

function lookup(dict, raw) {
  const text = raw.trim();
  if (!text || text.length > 200) return null;
  if (!FOLDED) {
    FOLDED = new Map();
    for (const k of Object.keys(dict)) FOLDED.set(k.toLowerCase(), dict[k]);
  }
  const hit = dict[text] || FOLDED.get(text.toLowerCase());
  if (!hit || hit === text) return null;
  return raw.replace(text, hit);
}

function translateText(node, dict) {
  const original = ORIGINAL.get(node) ?? node.nodeValue;
  const next = lookup(dict, original);
  if (next && node.nodeValue !== next) {
    ORIGINAL.set(node, original);
    TOUCHED.push({ node });
    node.nodeValue = next;
  }
}

function translateAttrs(el, dict) {
  for (const attr of ATTRS) {
    const cur = el.getAttribute(attr);
    if (!cur) continue;
    const store = ORIGINAL.get(el) || {};
    const original = store[attr] ?? cur;
    const next = lookup(dict, original);
    if (next && cur !== next) {
      store[attr] = original;
      ORIGINAL.set(el, store);
      TOUCHED.push({ node: el, attr });
      el.setAttribute(attr, next);
    }
  }
}

function walk(root, dict) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const el = n.parentElement;
      if (!el) return NodeFilter.FILTER_ACCEPT;
      if (SKIP_TAGS.has(el.tagName) || el.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((n) => translateText(n, dict));
  root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => {
    if (!el.closest(SKIP_SELECTOR)) translateAttrs(el, dict);
  });
}

/** Start transliterating the page. Returns a stop function. */
export function startDomTranslate(dict) {
  let queued = false;
  const observer = new MutationObserver(() => schedule());
  const observe = () => observer.observe(document.body, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ATTRS,
  });
  const run = () => {
    queued = false;
    observer.disconnect();
    try { walk(document.body, dict); } finally { observe(); }
  };
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  }

  run();
  return () => {
    observer.disconnect();
    for (const { node, attr } of TOUCHED) {
      const store = ORIGINAL.get(node);
      if (store === undefined) continue;
      if (attr) { if (store[attr] != null) node.setAttribute(attr, store[attr]); }
      else node.nodeValue = store;
    }
    TOUCHED = [];
  };
}
